import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Users, 
    UserCheck, 
    Calendar,
    Clock,
    ChevronRight,
    Home,
    Shield,
    ShieldAlert,
    Ban,
    Activity
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetricCard = ({ title, value, icon: Icon, color, link }) => (
    <Link to={link} className="block">
        <Card className="metric-card hover-lift cursor-pointer group">
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm text-muted-foreground mb-1">{title}</p>
                        <p className="text-3xl font-bold">{value}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${color} group-hover:scale-110 transition-transform duration-200`}>
                        <Icon className="w-6 h-6 text-white" />
                    </div>
                </div>
            </CardContent>
        </Card>
    </Link>
);

const ActivityItem = ({ activity }) => {
    const typeColors = {
        incident: 'bg-red-500',
        training: 'bg-blue-500',
        meeting: 'bg-purple-500',
        exercise: 'bg-amber-500'
    };
    
    return (
        <div className={`p-4 rounded-lg border-l-4 ${typeColors[activity.activity_type]} bg-card border border-border`}>
            <div className="flex items-start justify-between">
                <div>
                    <h4 className="font-medium">{activity.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                        {format(new Date(activity.start_time), 'MMM d, h:mm a')}
                    </p>
                    {activity.location && (
                        <p className="text-sm text-muted-foreground">{activity.location}</p>
                    )}
                </div>
                <Badge variant="outline" className="capitalize">
                    {activity.activity_type}
                </Badge>
            </div>
            <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                    {activity.responses?.length || 0} responses
                </span>
            </div>
        </div>
    );
};

const ActiveMemberItem = ({ member }) => (
    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-green-500 status-pulse"></div>
            <div>
                <p className="font-medium text-sm">{member.user_name}</p>
                <p className="text-xs text-muted-foreground">
                    Clocked in {formatDistanceToNow(new Date(member.clock_in), { addSuffix: true })}
                    {member.is_duty_officer && ' (Duty Officer)'}
                </p>
            </div>
        </div>
        {member.activity_name && (
            <Badge variant="secondary" className="text-xs">
                {member.activity_name}
            </Badge>
        )}
    </div>
);

const SecurityEventItem = ({ incident }) => {
    const severityColors = {
        CRITICAL: 'bg-red-500 text-white',
        HIGH: 'bg-orange-500 text-white',
        MEDIUM: 'bg-amber-500 text-white',
        LOW: 'bg-blue-500 text-white'
    };
    
    const typeLabels = {
        brute_force: 'Brute Force',
        path_enumeration: 'Path Enumeration', 
        nosql_injection: 'NoSQL Injection',
        xss_attempt: 'XSS Attempt',
        rapid_requests: 'Rapid Requests',
        login_failure: 'Login Failure',
        INVALID_SIGNATURE: 'Invalid Signature',
        RATE_LIMIT_EXCEEDED: 'Rate Limit Exceeded'
    };
    
    const incidentType = incident.type || incident.incident_type;
    const createdAt = incident.timestamp || incident.created_at;
    
    return (
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border-l-4 border-l-red-500">
            <div className="flex items-center gap-3">
                <ShieldAlert className="w-4 h-4 text-red-500" />
                <div>
                    <p className="font-medium text-sm">{typeLabels[incidentType] || incidentType}</p>
                    <p className="text-xs text-muted-foreground">
                        IP: {incident.ip_address} • {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
                    </p>
                </div>
            </div>
            <Badge className={`text-xs ${severityColors[incident.severity] || 'bg-gray-500'}`}>
                {incident.severity}
            </Badge>
        </div>
    );
};

export default function DashboardPage() {
    const navigate = useNavigate();
    const { user, isNetworkManager } = useAuth();
    const [stats, setStats] = useState(null);
    const [activities, setActivities] = useState([]);
    const [activeMembers, setActiveMembers] = useState([]);
    const [securityStats, setSecurityStats] = useState(null);
    const [securityIncidents, setSecurityIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        fetchDashboardData();
    }, []);
    
    const fetchDashboardData = async () => {
        try {
            // Fetch core dashboard data
            const [statsRes, activitiesRes, activeRes] = await Promise.all([
                axios.get(`${API_URL}/dashboard/stats`),
                axios.get(`${API_URL}/activities`),
                axios.get(`${API_URL}/attendance/active`)
            ]);
            
            setStats(statsRes.data);
            setActivities(activitiesRes.data.slice(0, 5));
            setActiveMembers(activeRes.data.slice(0, 5));
            
            // Fetch security data separately for network managers (non-blocking)
            if (isNetworkManager()) {
                try {
                    const [secStatsRes, secIncidentsRes] = await Promise.all([
                        axios.get(`${API_URL}/admin/security/stats`),
                        axios.get(`${API_URL}/admin/security/incidents`, { params: { limit: 5 } })
                    ]);
                    setSecurityStats(secStatsRes.data);
                    const incidentsData = secIncidentsRes.data.incidents || secIncidentsRes.data || [];
                    setSecurityIncidents(incidentsData.slice(0, 5));
                } catch (secError) {
                    console.warn('Failed to fetch security data:', secError);
                    // Security data is optional, don't fail the whole dashboard
                }
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            toast.error('Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };
    
    if (loading) {
        return (
            <div className="space-y-6 animate-fade-in">
                <div className="dashboard-grid">
                    {[...Array(4)].map((_, i) => (
                        <Card key={i} className="h-32">
                            <CardContent className="p-6">
                                <div className="skeleton h-4 w-24 mb-2 rounded"></div>
                                <div className="skeleton h-8 w-16 rounded"></div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
            {/* Header with back to Portal */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h2 className="text-2xl lg:text-3xl font-bold">
                        Admin Dashboard
                    </h2>
                    <p className="text-muted-foreground mt-1">
                        Unit overview and management
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => navigate('/portal')}
                    className="gap-2"
                    data-testid="back-to-hub-btn"
                >
                    <Home className="w-4 h-4" />
                    Back to Portal
                </Button>
            </div>
            
            {/* Metrics Grid */}
            <div className="dashboard-grid">
                <MetricCard
                    title="Total Members"
                    value={stats?.total_members || 0}
                    icon={Users}
                    color="bg-secondary"
                    link="/admin/members"
                />
                <MetricCard
                    title="Active Now"
                    value={stats?.active_members || 0}
                    icon={UserCheck}
                    color="bg-green-600"
                    link="/admin/members"
                />
                <MetricCard
                    title="Upcoming Events"
                    value={stats?.upcoming_activities || 0}
                    icon={Calendar}
                    color="bg-primary"
                    link="/calendar"
                />
            </div>
            
            {/* Activities & Active Members */}
            <div className="bento-grid">
                <Card className="bento-span-2">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>Upcoming Activities</CardTitle>
                                <CardDescription>Events and incidents scheduled</CardDescription>
                            </div>
                            <Link to="/calendar">
                                <Button variant="outline" size="sm" data-testid="view-calendar-btn">
                                    View Calendar
                                </Button>
                            </Link>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {activities.length > 0 ? (
                            <div className="space-y-3">
                                {activities.map((activity) => (
                                    <ActivityItem key={activity.id} activity={activity} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No upcoming activities</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
                
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>Active Now</CardTitle>
                                <CardDescription>Members currently on duty</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                                <span className="text-sm font-medium">{activeMembers.length}</span>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {activeMembers.length > 0 ? (
                            <div className="space-y-2">
                                {activeMembers.map((member) => (
                                    <ActiveMemberItem key={member.id} member={member} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No active members</p>
                            </div>
                        )}
                        <Link to="/attendance" className="block mt-4">
                            <Button variant="ghost" className="w-full justify-between" data-testid="view-attendance-btn">
                                View all attendance
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
            
            {/* Security Events Panel - Network Manager Only */}
            {isNetworkManager() && securityStats && (
                <Card className="border-red-200 dark:border-red-900">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                                    <Shield className="w-5 h-5 text-red-600 dark:text-red-400" />
                                </div>
                                <div>
                                    <CardTitle className="text-lg">Security Events</CardTitle>
                                    <CardDescription>Real-time blocked attack attempts</CardDescription>
                                </div>
                            </div>
                            <Link to="/admin/security">
                                <Button variant="outline" size="sm" className="border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/30" data-testid="view-security-btn">
                                    <Shield className="w-4 h-4 mr-1" />
                                    Security Dashboard
                                </Button>
                            </Link>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {/* Security Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-center">
                                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{securityStats.total_incidents || 0}</p>
                                <p className="text-xs text-muted-foreground">Total Incidents</p>
                            </div>
                            <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-center">
                                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{securityStats.incidents_last_24h || 0}</p>
                                <p className="text-xs text-muted-foreground">Last 24 Hours</p>
                            </div>
                            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center">
                                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{securityStats.blocked_ips_count || 0}</p>
                                <p className="text-xs text-muted-foreground">Blocked IPs</p>
                            </div>
                            <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-center">
                                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{securityStats.by_status?.NEW || 0}</p>
                                <p className="text-xs text-muted-foreground">Unresolved</p>
                            </div>
                        </div>
                        
                        {/* Recent Incidents */}
                        {securityIncidents.length > 0 ? (
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-muted-foreground mb-3">Recent Blocked Attempts</p>
                                {securityIncidents.map((incident) => (
                                    <SecurityEventItem key={incident.id} incident={incident} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6 text-muted-foreground">
                                <Shield className="w-10 h-10 mx-auto mb-2 opacity-50 text-green-500" />
                                <p className="text-green-600 dark:text-green-400 font-medium">All Clear</p>
                                <p className="text-sm">No security incidents detected</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
